class GPTDialogCtrl {
  constructor(FEService, TemplateService, EventService, $element, $timeout, AuthService, StorageService, $uibModal, $sce) {
    'ngInject';
    this.fe = FEService;
    this.ts = TemplateService;
    this.es = EventService;
    this.as = AuthService;
    this.ss = StorageService;
    this.$element = $element;
    this.$timeout = $timeout;
    this.$uibModal = $uibModal;
    this.$sce = $sce;

    // UI state
    this.inputMessage = '';
    this.messages = []; // { role: 'user'|'assistant', text: string, ts: Date }
    this.loading = false;
    this.error = null;
    this.COLLAPSE_THRESHOLD = 200; // characters
    this.userDisplayName = "-Loading-";
    this.syncFields = [];
    // Prompt sent to messageLLM to get the initial assistant greeting (no new endpoint)
    this.FALLBACK_GREETING = `Hi! I'm Spark, your Template building assistant. Tell me what you want your Template to look like, and I'll get started.`;
  }

  async $onInit() {
    this.userDisplayName = this.as.getProfile()?.displayName;
    // Placeholder while we fetch the initial greeting from messageLLM
    this.messages.push(this.buildMessage('assistant', '- Loading -'));
    this.fetchInitialMessage();
  }

  removeMargin(layout) {
    return layout
      .replace(/(?:^|;)\s*margin\s*:\s*[^;]*\s*;?/i, ';') // remove margin decl
      .replace(/;{2,}/g, ';')                            // collapse ;;
      .replace(/^\s*;\s*|\s*;\s*$/g, '');                // trim leading/trailing ;
  }

  /** Calls messageLLM with a one-off greeting prompt and sets the first assistant message. */
  async fetchInitialMessage() {
    if (!this.tool || !this.tool.id) {
      this.messages[0] = this.buildMessage('assistant', this.FALLBACK_GREETING, { expand: true });
      this.scrollToBottom();
      return;
    }
    try {
      // const json = JSON.stringify(this.ts.toLLMDTO(), null, 2);
      const json = JSON.stringify({}, null, 2);
      const blob = new Blob([json], { type: "application/json" });

      const jsonFilePath = await this.ss.uploadTempFile(blob, '', `json`);

      const request = {
        messages: [],
        jsonFilePath,
        tool: this.tool.id
      };
      const res = await this.fe.httpsCallable('messageLLM', request);

      const data = (res && res.data) ? res.data : {};
      const assistantMessages = data.assistantMessages || [];
      const message = data.message;
      if (assistantMessages.length > 0) {
        this.messages = assistantMessages.map((m, i) =>
          this.buildMessage('assistant', m.content || '', { expand: i === 0 })
        );
      } else {
        this.messages[0] = this.buildMessage('assistant', String(message || this.FALLBACK_GREETING), { expand: true });
      }
    } catch (err) {
      console.warn('Initial message from messageLLM failed, using fallback:', err);
      this.messages[0] = this.buildMessage('assistant', this.FALLBACK_GREETING, { expand: true });
    }
    this.scrollToBottom();
  }

  $onDestroy() {
    this.closeBlockingModal();
  }

  buildMessage(role, text, options = {}) {
    const full = String(text || '');
    const long = full.length > this.COLLAPSE_THRESHOLD;
    const preview = long
      ? full.slice(0, this.COLLAPSE_THRESHOLD).trimEnd() + '…'
      : full;

    return {
      role,                      // 'user' | 'assistant'
      content: full,                // full text
      preview,                   // truncated preview (or full text if short)
      long,                      // is longer than threshold
      expanded: long && !!options.expand,  // first message starts expanded when long
      ts: new Date()
    };
  }

  renderText(m) {
    return (m.expanded || !m.long) ? m.content : m.preview;
  }

  toggle(m, $event) {
    if (m.long) {
      m.expanded = !m.expanded;
      if ($event) $event.stopPropagation();
    }
  }

  // Template is linked; safe to query DOM
  $postLink() {
    this.historyEl = this.$element[0].querySelector('#gpt-dialog-history');
  }

  scrollToBottom() {
    // wait for DOM to update, then scroll
    this.$timeout(() => {
      const el = this.historyEl;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }


  async sendMessage() {
    await this.send();
  }

  async send() {
    if (this.loading) return;
    const text = (this.inputMessage || '').trim();
    if (!text) return;
    this.inputMessage = '';

    // push user message to the thread
    this.messages.push(this.buildMessage('user', text));
    this.scrollToBottom();

    this.loading = true;
    this.error = null;

    // OPEN the unclosable 3-minute progress modal
    this._blockingModal = this.openBlockingProgressModal({
      title: `${this.tool.id === 'createTemplateFromPrompt' ? 'Creating' : 'Updating'} Template…`,
      message: 'Hang tight - this may take a couple minutes.',
      totalMs: 120000,
      maxMs: 180000
    });

    if (this.ts.template.layout?.includes('margin')) {
      this.ts.template.layout = this.removeMargin(this.ts.template.layout);
    }

    try {

      const json = JSON.stringify(this.ts.toLLMDTO(), null, 2);
      const blob = new Blob([json], { type: "application/json" });

      const jsonFilePath = await this.ss.uploadTempFile(blob, '', `json`);

      // const newMessage = {content: text, role: "user"};

      const request = {
        messages: this.messages,
        jsonFilePath,
        tool: this.tool.id
      };

      const res = await this.fe.httpsCallable('messageLLM', request);

      const { template, message, syncFields } = (res && res.data) ? res.data : {};
      // syncFields is an array of { reference, field, placeholderText } objects
      if (syncFields) {
        this.syncFields = (syncFields || []).map(({ referenceId, fieldId, placeholderText }) => {
          const reference = this.ts.template.references.find(r => r.id === referenceId);
          const field = reference?.tableSchema?.fields.find(f => f.id === fieldId);
          return { reference, field, placeholderText };
        });
      }

      if (!!template) {
        this.es.dispatchEvent(this.es.actions.TEMPLATE_CONTENT_UPDATED, { content: template, layout: this.ts.template.layout });
      }

      this.messages.push(this.buildMessage('assistant', String(message || '[empty reply]')));
      this.scrollToBottom();

    } catch (err) {
      const serverMsg =
        (err && err.data && (err.data.error || err.data.message)) ||
        (err && err.message);
      this.error = serverMsg || 'Failed to send message.';
      console.error('messageLLM failed:', err);
      this.messages.push(this.buildMessage('assistant', String(serverMsg || '[unknown error]')));
    } finally {
      this.loading = false;

      // CLOSE the modal as soon as the request completes (or ignore if timer already closed it)
      this.closeBlockingModal();
      // If we were in create mode, ask parent to switch to update mode
      if (this.tool && this.tool.id === 'createTemplateFromPrompt' && this.onTemplateCreated) {
        // async/await can escape digest; $timeout ensures UI updates
        this.$timeout(() => this.onTemplateCreated(), 0);
      }
    }
  }

  swapPlaceholders() {
    this.es.dispatchEvent(this.es.actions.TEMPLATE_SWAP_PLACEHOLDER_TOKENS, {
      syncFields: this.syncFields // syncFields is an array of { reference, field, placeholderText } objects
    });
  }

  showLastStep() {
    return this.tool.id === 'updateTemplateFromPrompt' && this.syncFields.length > 0;
  }

  // Allow Ctrl/Cmd+Enter to send from textarea
  keydown($event) {
    if (($event.ctrlKey || $event.metaKey) && $event.key === 'Enter') {
      $event.preventDefault();
      this.send();
    }
  }

  openBlockingProgressModal(opts) {
    const cfg = Object.assign({
      title: 'Working…',
      message: `Generating your template. Please don't close this window.`,
      totalMs: 120000, // 2 minutes
      maxMs: 180000
    }, opts || {});

    return this.$uibModal.open({
      animation: true,
      backdrop: 'static',  // cannot close by clicking outside
      keyboard: false,     // cannot close with ESC
      windowClass: 'gpt-blocking-modal', // optional hook for styling
      resolve: {
        modalConfig: function () { return cfg; }
      },
      template: [
        '<div class="modal-header">',
        '  <h3 class="modal-title">{{vm.title}}</h3>',
        '</div>',
        '<div class="modal-body">',
        '  <p class="text-muted" style="margin-bottom:10px;">{{vm.message}}</p>',
        '  <div class="progress">',
        '    <div class="progress-bar progress-bar-striped active"',
        '         role="progressbar"',
        '         aria-valuemin="0" aria-valuemax="100"',
        '         aria-valuenow="{{vm.percent}}"',
        '         ng-style="{ width: vm.percent + \'%\' }">',
        '      <span class="sr-only">{{vm.percent}}% Complete</span>',
        '    </div>',
        '  </div>',
        '  <div class="clearfix">',
        '    <div class="pull-left text-muted">{{vm.remaining}}s remaining</div>',
        '    <div class="pull-right text-muted">{{vm.percent}}%</div>',
        '  </div>',
        '</div>'
      ].join(''),
      controllerAs: 'vm',
      bindToController: true,
      controller: [
        '$scope', '$interval', '$uibModalInstance', 'modalConfig',
        function ($scope, $interval, $uibModalInstance, modalConfig) {
          var vm = this;

          vm.title = modalConfig.title;
          vm.message = modalConfig.message;

          var totalMs = modalConfig.totalMs;
          var maxMs = modalConfig.maxMs;
          var tickMs = 200;
          var start = Date.now();

          vm.percent = 0;
          vm.remaining = Math.ceil(totalMs / 1000);

          var timer = $interval(function () {
            var elapsed = Date.now() - start;
            if (elapsed > maxMs) vm.message = 'This is taking much longer than usual...';
            else if (elapsed > totalMs) vm.message = 'Sorry, this is taking awhile...';

            if (elapsed > totalMs) elapsed = totalMs;

            vm.percent = Math.round((elapsed / totalMs) * 100);
            vm.remaining = Math.max(0, Math.ceil((totalMs - elapsed) / 1000));

            // below is unnecessary because function timeout (5 minutes) will 
            // move us into the 'finally' block above and close the modal
            // if (elapsed >= totalMs) {
            // $interval.cancel(timer);
            // timer = null;
            // // Timer completed: close programmatically (still "unclosable" by user)
            // $uibModalInstance.close('timeout');
            //   }
          }, tickMs);

          $scope.$on('$destroy', function () {
            if (timer) $interval.cancel(timer);
          });
        }
      ]
    });
  }

  closeBlockingModal() {
    if (!this._blockingModal) return;
    try {
      this._blockingModal.close('done');
    } catch (e) {
      // ignore if already closed (e.g., timer hit)
    } finally {
      this._blockingModal = null;
    }
  }
}

export default {
  bindings: {
    tool: '<',
    onTemplateCreated: '&?',
    syncFields: '='
  },
  templateUrl: 'dynamic-builder/gpt-dialog/gpt-dialog.component.html',
  controller: GPTDialogCtrl
};
